import React from "react";

const RESULT_STYLE = {
  NEW: "bg-emerald-50 text-emerald-700 border-emerald-200",
  DUPLICATE: "bg-zinc-100 text-zinc-600 border-zinc-300",
  REVIEW: "bg-amber-50 text-amber-700 border-amber-200",
  ERROR: "bg-red-50 text-red-700 border-red-200",
};

const COUNT_FIELDS = [
  ["source_rows", "Source rows"],
  ["parsed_rows", "Parsed"],
  ["new", "New"],
  ["duplicates", "Duplicates"],
  ["review", "Review"],
  ["errors", "Errors"],
];

const money = (v, cur) => (v === null || v === undefined ? "—" : `${cur || ""} ${Number(v).toFixed(2)}`.trim());
const margin = (v, uom) => (v === null || v === undefined ? "—" : `${Number(v).toFixed(2)}¢/${uom || "unit"}`);

const PreflightCounts = ({ counts }) => (
  <div className="grid grid-cols-3 sm:grid-cols-6 gap-1" data-testid="preflight-counts">
    {COUNT_FIELDS.map(([k, label]) => (
      <div key={k} className="border border-zinc-200 px-2 py-1 text-center">
        <div className="text-[10px] uppercase tracking-wide text-zinc-500">{label}</div>
        <div className="font-mono text-sm" data-testid={`preflight-count-${k}`}>{counts?.[k] ?? 0}</div>
      </div>
    ))}
  </div>
);

const PreflightRow = ({ r }) => (
  <tr className="border-t border-zinc-100 align-top" data-testid={`preflight-row-${r.row}`}>
    <td className="px-1 py-1">
      <span className={`inline-block border px-1 text-[10px] font-semibold ${RESULT_STYLE[r.result] || ""}`} data-testid={`preflight-row-result-${r.row}`}>{r.result}</span>
    </td>
    <td className="px-1 py-1 whitespace-nowrap">{r.tran_date || "—"}</td>
    <td className="px-1 py-1 font-mono">{r.reference || "—"}</td>
    <td className="px-1 py-1 font-mono">{r.card || "—"}</td>
    <td className="px-1 py-1 whitespace-nowrap">{[r.network, r.country].filter(Boolean).join(" · ")}</td>
    <td className="px-1 py-1">
      <div>{r.product || r.product_source || "—"}</div>
      {r.product_source && r.product_source !== r.product && <div className="text-zinc-400">src: {r.product_source}</div>}
    </td>
    <td className="px-1 py-1 whitespace-nowrap">{r.qty ?? "—"} {r.uom}</td>
    <td className="px-1 py-1">{r.customer_id ? (r.customer_name || r.customer_id) : <span className="text-amber-700">Unmatched</span>}</td>
    <td className="px-1 py-1 whitespace-nowrap">{money(r.supplier_amount, r.currency)}</td>
    <td className="px-1 py-1 whitespace-nowrap">{margin(r.margin_cpl, r.uom)}</td>
    <td className="px-1 py-1 whitespace-nowrap">{money(r.customer_amount, r.currency)}</td>
    <td className="px-1 py-1 text-zinc-600">{(r.reasons || []).join("; ")}</td>
  </tr>
);

export const PreflightPanel = ({ preflight, ack, setAck }) => {
  if (!preflight) return null;
  const { counts, critical = [], rows = [], requires_confirmation } = preflight;
  const issues = rows.filter((r) => r.result === "ERROR" || r.result === "REVIEW");
  return (
    <div className="space-y-2 border border-zinc-200 p-2" data-testid="preflight-panel">
      <div className="text-xs font-semibold">Validation result · {preflight.filename}</div>
      <PreflightCounts counts={counts} />
      {critical.length > 0 && (
        <div className="border border-red-300 bg-red-50 p-2 text-xs text-red-700" data-testid="preflight-critical">
          <b>Import blocked.</b>
          <ul className="list-disc ml-4">{critical.map((c) => <li key={c.code}>{c.code}: {c.message}</li>)}</ul>
        </div>
      )}
      {rows.length > 0 && (
        <div className="max-h-64 overflow-auto border border-zinc-100">
          <table className="w-full text-[11px]" data-testid="preflight-rows-table">
            <thead className="bg-zinc-50 text-zinc-500 sticky top-0">
              <tr>{["Result", "Date", "Doc #", "Card", "Network", "Product", "Qty", "Customer", "Supplier", "Margin", "Customer amt", "Reason"].map((h) => <th key={h} className="px-1 py-1 text-left font-medium">{h}</th>)}</tr>
            </thead>
            <tbody>{rows.map((r) => <PreflightRow key={r.row} r={r} />)}</tbody>
          </table>
        </div>
      )}
      {preflight.rows_truncated && <div className="text-[11px] text-zinc-500">Showing first {rows.length} rows.</div>}
      {requires_confirmation && critical.length === 0 && (
        <label className="flex items-start gap-2 text-xs border border-amber-300 bg-amber-50 p-2" data-testid="preflight-ack-label">
          <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} data-testid="preflight-ack-checkbox" />
          <span>
            I understand {counts.errors} row(s) will be recorded as failed (not imported as transactions) and {counts.review} row(s)
            will be imported as flagged/unmatched for review{issues.length ? ` (rows ${issues.slice(0, 10).map((r) => r.row).join(", ")}${issues.length > 10 ? "…" : ""})` : ""}.
            This import will be reported as completed with failures, not a clean success.
          </span>
        </label>
      )}
    </div>
  );
};

export const reconcileImport = (expected, actual) => {
  if (!expected) return [];
  const diffs = [];
  if (Number(actual.inserted ?? -1) !== expected.inserted_new) diffs.push(`inserted ${actual.inserted} vs expected ${expected.inserted_new}`);
  if (Number(actual.duplicates ?? 0) !== expected.duplicates) diffs.push(`duplicates ${actual.duplicates} vs expected ${expected.duplicates}`);
  if (Number(actual.failed ?? 0) !== expected.failed) diffs.push(`failed ${actual.failed} vs expected ${expected.failed}`);
  return diffs;
};
